import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, isRetriableApiError } from "../client";
import type { LoginRequest, MeResponse, OkResponse } from "../types";

// API_CONTRACT.md doesn't spell out POST /api/auth/login's success response body
// (only that it sets the session cookie + writes audit_log). We don't assume a
// shape — on success we just invalidate /auth/me and let the existing useMe()
// query re-fetch the authoritative user object.

const ME_KEY = ["auth", "me"] as const;

// GET /api/auth/me — 401 means "not logged in", which we treat as a normal
// (non-error) query result of `null` rather than a thrown error, so the app
// shell doesn't need to special-case react-query's error state for the
// single expected 401.
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const res = await api.get<MeResponse>("/auth/me");
        return res.user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

// react-query mutations don't retry by default (unlike queries) — deliberately
// opted back in here, scoped to network/gateway glitches only (see
// isRetriableApiError). The known failure mode isn't the server being slow;
// it's the client's own TCP connect sometimes getting silently dropped
// before reaching nginx at all (see docs/prod-environment.md) — a short,
// bounded retry recovers from that within a couple seconds in the common
// case instead of surfacing an error for something that would have worked
// on the very next attempt.
// Worst case (timeout hit on every attempt): 3 * 5s + 2 * 1s = 17s total —
// long for a login button, but bounded, and only the rare bad-window case
// pays it; the common case is one retry recovering within ~1-3s.
const LOGIN_TIMEOUT_MS = 5_000;
const LOGIN_MAX_RETRIES = 2;
const LOGIN_RETRY_DELAY_MS = 1_000;

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) =>
      api.post<OkResponse | MeResponse>("/auth/login", body, LOGIN_TIMEOUT_MS),
    retry: (failureCount, error) => failureCount < LOGIN_MAX_RETRIES && isRetriableApiError(error),
    retryDelay: LOGIN_RETRY_DELAY_MS,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<OkResponse>("/auth/logout"),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}
